import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Eye, Lock, Database, Users, CheckCircle } from 'lucide-react';
import Header from './Header';
import useScrollToTop from '../hooks/useScrollToTop';

const PrivacyPolicy = () => {
  const currentYear = new Date().getFullYear();
  useScrollToTop();

  const sections = [
    {
      icon: <Shield className="w-6 h-6 text-clara-blue-600" />,
      title: "Information We Collect",
      content: [
        "We collect information you provide directly to us, such as when you join our waitlist, including your email address.",
        "We may collect usage information about how you interact with our website, including IP address, browser type, and pages visited.",
        "We use cookies and similar technologies to enhance your experience and collect usage data."
      ]
    },
    {
      icon: <Eye className="w-6 h-6 text-clara-blue-600" />,
      title: "How We Use Your Information",
      content: [
        "To provide and maintain our services, including the ClaraAI waitlist and updates.",
        "To communicate with you about product updates, releases, and important announcements.",
        "To improve our website and services based on usage patterns and feedback.",
        "To comply with legal obligations and protect our rights and safety."
      ]
    },
    {
      icon: <Lock className="w-6 h-6 text-clara-blue-600" />,
      title: "Data Security",
      content: [
        "We implement appropriate security measures to protect your personal information.",
        "Your data is encrypted in transit and at rest using industry-standard protocols.",
        "We regularly review and update our security practices to maintain data protection.",
        "Access to your personal information is limited to authorized personnel only."
      ]
    },
    {
      icon: <Database className="w-6 h-6 text-clara-blue-600" />,
      title: "Data Retention",
      content: [
        "We retain your information for as long as necessary to provide our services.",
        "You may request deletion of your data at any time by contacting us.",
        "Some information may be retained for legal or legitimate business purposes.",
        "We will notify you of any changes to our data retention practices."
      ]
    },
    {
      icon: <Users className="w-6 h-6 text-clara-blue-600" />,
      title: "Third-Party Services",
      content: [
        "We use SendGrid for email delivery and management.",
        "Our website is hosted on secure cloud infrastructure.",
        "We do not sell, trade, or rent your personal information to third parties.",
        "Third-party services are bound by their own privacy policies and our data protection agreements."
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>ClaraAI Privacy Policy</title>
        <meta name="description" content="Read ClaraAI's privacy policy — privacy‑first design with control over shared data and local processing options." />
        <link rel="canonical" href="https://claraai.app/privacy" />
      </Helmet>
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-20 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.3) 0%, transparent 50%),
                              radial-gradient(circle at 75% 75%, rgba(147, 51, 234, 0.3) 0%, transparent 50%)`,
            backgroundSize: '100% 100%'
          }}></div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-20 left-1/3 w-24 h-24 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="text-left mb-8">
              <button
                onClick={() => window.history.back()}
                className="inline-flex items-center text-white hover:text-blue-300 font-medium transition-colors duration-200"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </button>
            </div>
            
            <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white font-medium text-sm mb-6">
              <Shield className="w-4 h-4 mr-2" />
              Privacy & Security
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              We are committed to protecting your privacy and ensuring the security of your personal information. 
              This policy explains how we collect, use, and safeguard your data.
            </p>
            <p className="text-sm text-gray-400 mt-4">
              Last updated: {currentYear}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="mb-16"
              >
                <div className="relative">
                  {/* Background decoration */}
                  <div className="absolute inset-0 bg-gradient-to-r from-clara-blue-50/50 to-purple-50/50 rounded-3xl blur-3xl"></div>
                  
                  {/* Content card */}
                  <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-gray-100 shadow-xl">
                    <div className="flex items-start space-x-6 mb-6">
                      <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-clara-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                        {section.icon}
                      </div>
                      <div className="flex-1">
                        <h2 className="text-3xl font-bold text-clara-gray-900 mb-6">
                          {section.title}
                        </h2>
                        <ul className="space-y-4">
                          {section.content.map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-start space-x-4">
                              <div className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-clara-blue-500 to-purple-600 rounded-full flex items-center justify-center mt-1">
                                <CheckCircle className="w-3 h-3 text-white" />
                              </div>
                              <span className="text-clara-gray-700 leading-relaxed text-lg">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}

            {/* Contact Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="relative"
            >
              {/* Background decoration */}
              <div className="absolute inset-0 bg-gradient-to-r from-clara-blue-50/50 to-purple-50/50 rounded-3xl blur-3xl"></div>
              
              {/* Contact card */}
              <div className="relative bg-gradient-to-r from-clara-blue-600 to-purple-600 rounded-3xl p-10 border border-clara-blue-200 shadow-2xl text-white">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl mb-6">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold mb-4">
                    Questions About Privacy?
                  </h2>
                  <p className="text-xl text-blue-100 leading-relaxed">
                    We're here to help. Contact us with any questions about this Privacy Policy or our data practices.
                  </p>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                    <h3 className="text-lg font-semibold mb-3">Email Support</h3>
                    <p className="text-blue-100 mb-2">Get in touch via email</p>
                    <a href="mailto:hello@claraai.app" className="text-white font-semibold hover:text-blue-200 transition-colors">
                      hello@claraai.app
                    </a>
                  </div>
                  
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                    <h3 className="text-lg font-semibold mb-3">Social Media</h3>
                    <p className="text-blue-100 mb-2">Follow us on X (Twitter)</p>
                    <a href="https://x.com/RoccoScalps" target="_blank" rel="noopener noreferrer" className="text-white font-semibold hover:text-blue-200 transition-colors">
                      @RoccoScalps
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy; 